/**
 * "Find your path": the cert sales call, as a few taps (Coleman, 2026-11-02).
 *
 * Not everyone buys for the same reason, so the first question is WHY they're
 * here, and the result is built around that:
 *   - brand:      they want to learn from By Any Means, the name is the point
 *   - everything: they want one big course that covers it all
 *   - specific:   they have a problem to fix (only these get the problem step)
 *   - credential: they want the credential for their career or business
 * Then who they coach and how much time they have, which sets the example
 * plan and the timeline. Pure data and logic here; CertPathPage renders it.
 *
 * Every claim reuses the cert's own copy (constants.js) so the quiz can't
 * promise anything the course page doesn't.
 */
import { CERT_UNITS, CERT_UNIT_OUTCOMES, CERT_PERSONAS } from "./constants";

export const MOTIVES = [
  { id: "brand", label: "I want to learn the By Any Means way", hint: "You know the name and want the full system" },
  { id: "everything", label: "I want one course that covers everything", hint: "Shooting to psychology, all in one place" },
  { id: "specific", label: "I have specific things I want to fix", hint: "Pick them next and we'll show you where" },
  { id: "credential", label: "I want a credential to grow my career", hint: "Stand out to players, parents and programs" },
];

export const ROLES = [
  { id: "trainer", label: "I train individual players", persona: "pd" },
  { id: "team", label: "I coach a team (school, club, AAU)", persona: "team" },
  { id: "business", label: "I run or am building a training business", persona: "trainer" },
  { id: "growing", label: "I'm newer and want to get really good", persona: "growth" },
];

// What they want to fix -> the units that fix it. Units by CERT_UNITS id.
export const PROBLEMS = [
  { id: "transfer", label: "Players don't carry drills into games", units: [3, 1] },
  { id: "practice", label: "Designing better practices and sessions", units: [3, 9] },
  { id: "shooting", label: "Shooting", units: [2] },
  { id: "handle", label: "Ballhandling", units: [5] },
  { id: "finishing", label: "Finishing", units: [7] },
  { id: "iq", label: "Reading the game and basketball IQ", units: [4] },
  { id: "mindset", label: "Confidence and mindset", units: [8] },
  { id: "culture", label: "Culture, communication and parents", units: [6] },
  { id: "science", label: "Coaching with the latest science", units: [1, 9] },
];
export const MAX_PROBLEMS = 3;

export const TIME = [
  { id: "light", label: "1 to 3 hours a week", months: "6 to 8 months" },
  { id: "steady", label: "4 to 6 hours a week", months: "3 to 4 months" },
  { id: "fast", label: "7+ hours a week", months: "about 2 months" },
];

const unit = (id) => CERT_UNITS.find((u) => u.id === id);
// Some unit outcomes carry an em dash; coach-facing copy doesn't.
const noDash = (t) => String(t).replace(/\s*\u2014\s*/g, ": ");
const byId = (list, id) => list.find((x) => x.id === id) || null;

/** The steps to show, given the answers so far (problems only for "specific"). */
export function pathSteps(answers = {}) {
  return answers.motive === "specific" ? ["motive", "role", "problems", "time"] : ["motive", "role", "time"];
}

/** Turn answers into the result page's content. */
export function buildPath({ motive, role, problems = [], time } = {}) {
  const r = byId(ROLES, role);
  const persona = r ? byId(CERT_PERSONAS, r.persona) : null;
  const t = byId(TIME, time) || TIME[1];

  let headline;
  let intro;
  let focusIds;
  if (motive === "specific" && problems.length) {
    const picked = PROBLEMS.filter((p) => problems.includes(p.id));
    focusIds = [...new Set(picked.flatMap((p) => p.units))].slice(0, 4);
    headline = "Here's where the cert fixes it";
    const names = picked.map((p) => p.label);
    const list = names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0];
    intro = `You picked: ${list}. These are the units built for exactly that, and you get all nine.`;
  } else if (motive === "brand") {
    focusIds = [1, 3, 4];
    headline = "The By Any Means system, start to finish";
    intro = "Everything our own coaches use every day, organized into one course: the ideas behind it, how we design practice, and how we build players who read the game.";
  } else if (motive === "credential") {
    focusIds = role === "team" ? [3, 4, 6] : [1, 3, 6];
    headline = "Earn the BAM Certified credential";
    intro = "A real test, reviewed by our staff, so the credential means something. Plus the business resources and network that come with it.";
  } else {
    focusIds = [1, 2, 3, 5];
    headline = "Everything, in one course";
    intro = "Nine units from shooting to psychology, 750+ drills and workouts, and weekly calls with our team. One place for all of it.";
  }

  const focus = focusIds
    .map((id) => ({ id, title: unit(id)?.title || `Unit ${id}`, outcomes: (CERT_UNIT_OUTCOMES[id] || []).slice(0, 3).map(noDash) }))
    .filter((u) => u.title);

  return {
    headline,
    intro,
    focus,
    persona: persona ? { title: persona.title, desc: persona.desc } : null,
    timeline: `At ${t.label.toLowerCase()}, most coaches finish in ${t.months}. No deadline: go faster or slower.`,
    showCredentialExtras: motive === "credential" || role === "business",
  };
}

// "What's holding you back?" Each answer ends on Enroll and a call.
export const OBJECTIONS = [
  {
    id: "money",
    label: "It's a lot of money",
    points: [
      "Pay in full, in 3 monthly payments, or with Klarna where it's offered.",
      "Coaching outside the US? $400 off, applied automatically.",
      "6 months of the full BAM Coaches Platform is included.",
      "7-day refund window if it's not for you.",
      "Need-based scholarships exist. Ask us on a call.",
    ],
  },
  {
    id: "time",
    label: "I don't have much time",
    points: [
      "Self-paced with no deadline, and lifetime access.",
      "Most coaches spend 4 to 6 hours a week and finish in 3 to 4 months. Less time just means a longer path.",
      "Lessons are short videos you can do between sessions.",
    ],
  },
  {
    id: "level",
    label: "Is it right for my level?",
    points: [
      "Built for trainers, team coaches, business owners and newer coaches who want to get good fast.",
      "It starts from the foundations of how players learn, then builds up.",
      "The test has unlimited attempts, with feedback from our staff each time.",
    ],
  },
  {
    id: "trust",
    label: "I don't know By Any Means yet",
    points: [
      "Founded by Coleman Ayers, who has worked with coaches in 35+ countries and consulted from high school to NBA environments.",
      "Two books, both #1 in Basketball Coaching on Amazon.",
      "100+ coaches certified so far.",
      "Watch the video on the page to see inside the course.",
    ],
  },
  {
    id: "business",
    label: "Will it help my business or career?",
    points: [
      "The BAM Certified credential and badge.",
      "Business resources on pricing, positioning and program design.",
      "A global coach network, plus internship and retreat opportunities.",
      "Certified coaches can be invited to become Official BAM Coaches, who we refer business to.",
    ],
  },
  {
    id: "talk",
    label: "I'd rather talk to someone",
    points: ["Book a 20-minute call with our team. No pressure, no script."],
    call: true,
  },
];
